import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { BrandLogo } from './BrandLogo';
import WasslaWalletButton from './WasslaWalletButton';
import { COINS_DISABLED, isCoinNotification } from '../config/coinsFlag';
import { trackEvent } from '../utils/analytics';
import './brand.css';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import './Header.css';

import { useAppVersion } from '../version';

function Header() {
  const { t, i18n } = useTranslation();
  const { label: appVersionLabel } = useAppVersion();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Robust Arabic detection - check both i18n.language and document direction
  const lang = i18n.language || 'ar';
  const isArabic = lang.startsWith('ar') || document.documentElement.dir === 'rtl';
  
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Close notifications when route changes
  useEffect(() => {
    setShowNotifications(false);
    setClearDialogOpen(false); // Also close the clear dialog
  }, [location.pathname]);

  useEffect(() => {
    if (user && token) {
      fetchNotifications();
      fetchUnreadCount();
    } else {
      // Clear stale notifications on logout to prevent cross-user flash
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, token]);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notifications-dropdown') && !event.target.closest('.notification-btn')) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  const fetchNotifications = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/me`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setNotifications(COINS_DISABLED ? (Array.isArray(data) ? data.filter((n) => !isCoinNotification(n)) : data) : data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread/count`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/clear`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Optimistically clear UI
        setNotifications([]);
        setUnreadCount(0);
        setClearDialogOpen(false);
        
        // Confirm with backend
        setTimeout(() => {
          fetchNotifications();
          fetchUnreadCount();
        }, 500);
      } else {
        toast.error(t('errNetwork'));
      }
    } catch (error) {
      toast.error(t('errNetwork'));
    } finally {
      setClearing(false);
    }
  };

  const handleDeleteOne = async (notificationId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        // Remove from UI immediately
        setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
        
        // Update count
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  return (
    <header className="header" data-testid="header">
      {/* Build Version Label - for testing */}
      <div className="build-version" data-testid="build-version">
        {appVersionLabel}
      </div>
      
      <div className="header-content" dir={lang === 'ar' ? 'rtl' : 'ltr'} data-testid="home-header-content">
        {/* Logo — flush left */}
        <div className="header-logo-side" data-testid="home-header-logo-slot">
          <BrandLogo 
            variant="header"
          />
        </div>

        {/* Actions — flush right */}
        <div className="header-right-side">
          {user ? (
            <>
              <button
                onClick={() => {
                  const newLang = lang === 'ar' ? 'en' : 'ar';
                  i18n.changeLanguage(newLang);
                  localStorage.setItem('lang', newLang);
                  document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
                  document.documentElement.lang = newLang;
                }}
                className="lang-toggle"
                data-testid="language-toggle"
              >
                {lang === 'en' ? 'AR' : 'EN'}
              </button>

              <WasslaWalletButton coins={user.coins || 0} />

              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="header-icon-btn notification-btn"
                data-testid="notifications-button"
                aria-label="Notifications"
              >
                <span className="icon-inner">
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </span>
                {unreadCount > 0 && (
                  <span className="header-icon-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                const newLang = lang === 'ar' ? 'en' : 'ar';
                i18n.changeLanguage(newLang);
                localStorage.setItem('lang', newLang);
                document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
                document.documentElement.lang = newLang;
              }}
              className="lang-toggle"
              data-testid="language-toggle"
            >
              {lang === 'en' ? 'AR' : 'EN'}
            </button>
          )}
        </div>
      </div>

      {/* Notifications Dropdown */}
      {showNotifications && user && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h3 className="notifications-title">{t('notifTitle')}</h3>
            {notifications.length > 0 && (
              <button
                onClick={() => setClearDialogOpen(true)}
                className="clear-all-btn"
                data-testid="clear-all-notifications"
              >
                {t('notifClear')}
              </button>
            )}
          </div>
          <div className="notifications-list">
            {notifications.length === 0 ? (
              <div className="notification-item notification-empty">
                <div className="notification-empty-icon">🔔</div>
                <p className="notification-empty-text">{t('notifEmpty')}</p>
                <p className="notification-empty-hint">
                  {isArabic ? 'ستظهر الإشعارات هنا عند وصولها' : 'Notifications will appear here'}
                </p>
              </div>
            ) : (
              notifications.map((notif) => {
                // Determine notification title and body
                let displayTitle = '';
                let displayBody = '';
                
                if (notif.title) {
                  // New structured notification with title/body objects
                  displayTitle = isArabic 
                    ? (notif.title.ar || notif.title.en || notif.title) 
                    : (notif.title.en || notif.title.ar || notif.title);
                  displayBody = notif.body 
                    ? (isArabic ? (notif.body.ar || notif.body.en || notif.body) : (notif.body.en || notif.body.ar || notif.body))
                    : '';
                } else if (notif.message_code) {
                  // Code-based translation system
                  const params = notif.params || {};
                  switch (notif.message_code) {
                    case 'AD_PUBLISHED':
                      displayTitle = isArabic ? 'تم نشر الإعلان' : 'Ad Published';
                      displayBody = t('notification_ad_published', { title: params.title || '' });
                      break;
                    case 'AD_PENDING':
                      displayTitle = isArabic ? 'إعلان قيد المراجعة' : 'Ad Pending Review';
                      displayBody = t('notification_ad_pending', { title: params.title || '' });
                      break;
                    case 'AD_APPROVED':
                      displayTitle = isArabic ? 'تمت الموافقة على الإعلان' : 'Ad Approved';
                      displayBody = t('notification_ad_approved', { title: params.title || '' });
                      break;
                    case 'AD_REJECTED':
                      displayTitle = isArabic ? 'تم رفض الإعلان' : 'Ad Rejected';
                      displayBody = t('notification_ad_rejected', {
                        title: params.title || '',
                        reasonSuffix: params.reason
                          ? t('notification_ad_rejected_reason_suffix', { reason: params.reason })
                          : '',
                      });
                      break;
                    case 'AD_LIMITED':
                      displayTitle = isArabic ? 'إعلان محدود' : 'Ad Limited';
                      displayBody = t('notification_ad_limited', { title: params.title || '' });
                      break;
                    case 'AD_UNDER_REVIEW':
                    case 'AD_UNDER_REVIEW_AFTER_EDIT':
                      displayTitle = isArabic ? 'إعلان قيد المراجعة' : 'Under Review';
                      displayBody = t('notification_ad_under_review', { title: params.title || '' });
                      break;
                    case 'BOOKING_CONFIRMED':
                      displayTitle = isArabic ? 'تم تأكيد الحجز' : 'Booking Confirmed';
                      displayBody = t('notification_booking_confirmed', { type: params.type || '', date: params.date || '' });
                      break;
                    case 'ACCOUNT_BANNED':
                      displayTitle = isArabic ? 'تم إيقاف الحساب' : 'Account Suspended';
                      displayBody = t('notification_account_banned');
                      break;
                    case 'ACCOUNT_SUSPENDED':
                      displayTitle = isArabic ? 'تم تعليق الحساب' : 'Account Suspended';
                      displayBody = t('notification_account_suspended');
                      break;
                    case 'AD_REMOVED':
                      displayTitle = isArabic ? 'تم حذف الإعلان' : 'Ad Removed';
                      displayBody = t('notification_ad_removed', { title: params.title || '' });
                      break;
                    case 'AD_FLAGGED':
                      displayTitle = isArabic ? 'تم الإبلاغ عن الإعلان' : 'Ad Flagged';
                      displayBody = t('notification_ad_flagged', { title: params.title || '' });
                      break;
                    case 'WELCOME':
                      displayTitle = isArabic ? 'مرحباً بك في وصلة!' : 'Welcome to Wassla!';
                      displayBody = t('notification_welcome');
                      break;
                    case 'MESSAGE_RECEIVED':
                      displayTitle = isArabic ? 'رسالة جديدة' : 'New Message';
                      displayBody = t('notification_message_received', { from: params.from || '' });
                      break;
                    case 'REFERRAL_REWARD':
                      displayTitle = isArabic ? 'مكافأة الإحالة' : 'Referral Reward';
                      displayBody = isArabic 
                        ? `حصلت على ${params.reward || 10} عملات من إحالة صديق!`
                        : `You earned ${params.reward || 10} coins from a referral!`;
                      break;
                    case 'COINS_EARNED':
                      displayTitle = isArabic ? 'عملات جديدة' : 'Coins Earned';
                      displayBody = isArabic
                        ? `حصلت على ${params.amount || 0} عملات`
                        : `You earned ${params.amount || 0} coins`;
                      break;
                    case 'BOOST_ACTIVATED':
                      displayTitle = isArabic ? 'تم تفعيل التعزيز' : 'Boost Activated';
                      displayBody = isArabic
                        ? `تم تعزيز إعلانك "${params.title || ''}"`
                        : `Your ad "${params.title || ''}" has been boosted`;
                      break;
                    case 'BOOST_EXPIRED':
                      displayTitle = isArabic ? 'انتهى التعزيز' : 'Boost Expired';
                      displayBody = isArabic
                        ? `انتهت صلاحية تعزيز إعلانك "${params.title || ''}"`
                        : `Boost expired for "${params.title || ''}"`;
                      break;
                    default:
                      displayTitle = isArabic ? 'إشعار' : 'Notification';
                      displayBody = t('notification_generic');
                  }
                } else {
                  // Legacy fallback
                  displayTitle = isArabic ? 'إشعار' : 'Notification';
                  displayBody = isArabic 
                    ? (notif.message_ar || notif.message || '') 
                    : (notif.message_en || notif.message || '');
                }
                
                // Format relative time
                const formatRelativeTime = (dateStr) => {
                  const date = new Date(dateStr);
                  const now = new Date();
                  const diff = now - date;
                  const minutes = Math.floor(diff / 60000);
                  const hours = Math.floor(diff / 3600000);
                  const days = Math.floor(diff / 86400000);
                  
                  if (minutes < 1) return isArabic ? 'الآن' : 'Just now';
                  if (minutes < 60) return isArabic ? `منذ ${minutes} دقيقة` : `${minutes}m ago`;
                  if (hours < 24) return isArabic ? `منذ ${hours} ساعة` : `${hours}h ago`;
                  if (days < 7) return isArabic ? `منذ ${days} يوم` : `${days}d ago`;
                  return date.toLocaleDateString(isArabic ? 'ar-SD' : 'en-US', { month: 'short', day: 'numeric' });
                };

                // Get notification type icon
                const getTypeIcon = (type) => {
                  const icons = {
                    'wallet': '💰',
                    'ads': '📢',
                    'system': '⚙️',
                    'moderation': '🛡️',
                    'referral': '🎁',
                    'boost': '⚡',
                    'referral_reward': '🎁',
                    'welcome': '👋',
                    'message': '💬',
                  };
                  return icons[type] || '🔔';
                };
                
                return (
                  <div
                    key={notif.notification_id}
                    className={`notification-item ${notif.is_read ? 'read' : 'unread'} ${notif.type === 'saved_search_match' ? 'notification-clickable' : ''}`}
                    role={notif.type === 'saved_search_match' ? 'button' : undefined}
                    tabIndex={notif.type === 'saved_search_match' ? 0 : undefined}
                    onClick={notif.type === 'saved_search_match' ? () => {
                      // MVP log — fires when the user opens a saved-search
                      // alert from the bell. Only this notification type
                      // is clickable; all others keep their existing behavior.
                      trackEvent(
                        'alert_opened',
                        {
                          notification_id: notif.notification_id,
                          search_id: notif?.params?.saved_search_id || null,
                        },
                        user?.user_id
                      );
                      setShowNotifications(false);
                      navigate('/account/alerts');
                    } : undefined}
                    data-testid={`notification-item-${notif.notification_id}`}
                  >
                    <div className="notification-icon">
                      {getTypeIcon(notif.type)}
                    </div>
                    <div className="notification-content">
                      <p className="notification-title">{displayTitle}</p>
                      {displayBody && (
                        <p className="notification-body">{displayBody}</p>
                      )}
                      <span className="notification-time">
                        {formatRelativeTime(notif.created_at)}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteOne(notif.notification_id);
                      }}
                      className="notification-delete-btn"
                      data-testid={`delete-notif-${notif.notification_id}`}
                    >
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Clear All Confirmation Modal */}
      <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <AlertDialogContent dir={isArabic ? 'rtl' : 'ltr'} className="w-[92%] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('notifClearTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('notifClearMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel 
              disabled={clearing}
              className="btn btnSecondaryOrange"
            >
              {t('notifClearCancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                handleClearAll();
              }}
              disabled={clearing}
              className="btn btnPrimaryOrange"
            >
              {clearing ? t('loading') : t('notifClearConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}

export default Header;
